import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Building2, CheckCircle, Shield, Home, Users, Euro } from 'lucide-react';

interface BecomeHostDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BecomeHostDialog: FC<BecomeHostDialogProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleBecomeHost = async () => {
    if (!user) {
      toast.error('Bitte melde dich an, um fortzufahren');
      navigate('/auth');
      return;
    }

    setLoading(true);

    try {
      // Direkte Freischaltung als Host - wie in HostApplicationCard
      const { error } = await supabase
        .from('profiles')
        .update({
          host_approved: true,
          host_application_status: 'approved',
          user_type: 'host'
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Trigger cache invalidation
      window.dispatchEvent(new CustomEvent('profile-updated', { 
        detail: { type: 'host_approved', user_id: user.id } 
      }));

      toast.success('Du bist jetzt als Gastgeber freigeschaltet!', {
        description: 'Du kannst jetzt deine erste Unterkunft erstellen.'
      });
      onClose();
      
      // Direkt zur Unterkunft-Erstellung navigieren
      navigate('/create-listing');
    } catch (error) {
      console.error('Error becoming host:', error);
      toast.error('Fehler beim Freischalten als Gastgeber');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Building2 className="h-6 w-6 text-primary" />
            Gastgeber werden
          </DialogTitle>
          <DialogDescription className="text-base">
            Werde Teil der Fair-Stays Gemeinschaft und vermiete deine Unterkünfte fair und transparent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Benefits Section */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Deine Vorteile als Gastgeber
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Faire Provisionen ohne versteckte Kosten</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Vollständige Kontrolle über deine Preise und Buchungen</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Transparente Verwaltung und Statistiken</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <span>Genossenschafts-Modell mit Mitbestimmung</span>
              </li>
            </ul>
          </div>

          {/* Quick Info */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-primary/5 rounded-lg">
              <Home className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Sofort starten</p>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-lg">
              <Euro className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Keine Kosten</p>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-lg">
              <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">Gemeinschaft</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Später entscheiden
          </Button>
          <Button onClick={handleBecomeHost} disabled={loading} className="gap-2">
            {loading ? (
              'Wird aktiviert...'
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Jetzt Gastgeber werden
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
